import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsMobileView from '@/hooks/useIsMobileView';
import { twMerge } from 'tailwind-merge';
import { ReducerState } from '@/services/common/lib/reducerState';
import { BettingStructure } from '@/services/common/types/bettingStructure';
import ActionButton, { ActionButtonVariant } from '@/pages/table/components/ActionButton/ActionButton';
import QuickActionButtons from '@/pages/table/components/QuickActionButtons/QuickActionButtons';
import RaiseActionButton from '@/pages/table/components/RaiseActionButton/RaiseActionButton';
import CardFace, { CardSizes } from '@/components/CardFace/CardFace';
import {
  betAction,
  bringInAction,
  callAction,
  checkAction,
  completeAction,
  discardAction,
  foldAction,
  PlayerActions,
  playerSaveFoldCardsAction,
  raiseAction,
  TableIncomingEvents,
  tableStateUpdate,
  timebankAction,
} from '@/services/table/tableActions';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import BREAKPOINTS from '@/common/constant/breakpoints';
import BetSlider, { HandPhases } from '../../components/BetSlider/BetSlider';
import useAvailableActions from '../../hooks/useAvailableActions';
import TimeBankButton from '../../components/TimeBankButton/TimeBankButton';
import styles from './UserActions.module.css';
import getPreActions from '@/pages/table/utils/getPreActions';
import { AvailableActions } from '@/services/table/tableState';
import { Operations } from '@/services/table/tableOperations';
import { TableActionPerformedMsg } from '@/services/table/tableMessages';
import { getTableEventSound } from '@/services/table/tableUtils';
import useBBDisplay from '@/hooks/useBBDisplay';
import CurrencyLabel from '@/pages/table/components/CurrencyLabel/CurrencyLabel';

interface UserActionProps {
  tableId: string;
}

const defaultActions: AvailableActions = {
  canCollectBets: undefined,
  canPostAnte: undefined,
  canPostBlindOrStraddle: undefined,
  canBurnCard: undefined,
  canDealHole: undefined,
  canDealBoard: undefined,
  canStandPatOrDiscard: undefined,
  canPostBringIn: undefined,
  canShowOrMuckHoleCards: undefined,
  canKillHand: undefined,
  canPushChips: undefined,
  canPullChips: undefined,
  canWinNow: undefined,
  canFold: undefined,
  canCheck: undefined,
  canCall: undefined,
  canBet: undefined,
  canRaise: undefined,
  canCompleteBetOrRaise: undefined,
  canComplete: undefined,
};

const UserActions: FC<UserActionProps> = ({ tableId }) => {
  const isMobileView = useIsMobileView({ checkForUserAgent: true });
  const isMobileViewSm = useIsMobileView({ breakpoint: BREAKPOINTS.xs });
  const [showSlider, setShowSlider] = useState(isMobileView);
  const [preAction, setPreAction] = useState('');
  const [preActionCallAmount, setPreActionCallAmount] = useState<number | undefined>(undefined);

  const dispatch = useDispatch();

  const tableState = useSelector((state: ReducerState) => state.tables?.[tableId]);

  const playerSeat = tableState?.playerSeat;
  const seat = useMemo(
    () => tableState?.seats?.find((seat) => seat?.seat === tableState.playerSeat?.seat),
    [tableState.playerSeat?.seat, tableState?.seats],
  );
  const player = useSelector((state: ReducerState) => state.players[tableId]);
  const user = useSelector((state: ReducerState) => state.auth);
  const userHotkeys = useSelector((state: ReducerState) => state.auth.settings?.hotkeys);
  const isUsingTimebank = useMemo(() => tableState.seats.some((s) => s?.usingTimeBank), [tableState.seats]);

  const maxBet = useMemo(
    () => Math.max(...(tableState?.handState?.state?.bets || [])),
    [tableState?.handState?.state?.bets],
  );

  const getCardFromIndex = useCallback(
    (deckCardIndex: number) => {
      const cardIndex = playerSeat?.cardIndexes.indexOf(deckCardIndex);
      return playerSeat?.holeCards[cardIndex!]!;
    },
    [playerSeat],
  );

  const cardsForDiscard = useMemo(
    () => player?.cardIndexesForDiscard?.map(getCardFromIndex),
    [getCardFromIndex, player?.cardIndexesForDiscard],
  );

  const isActingPlayer = playerSeat?.isActingPlayer;
  const [foldModalVisible, setFoldModalVisible] = useState(false);

  const closeFoldModalHandler = useCallback(() => setFoldModalVisible(false), []);
  const openFoldModalHandler = useCallback(() => setFoldModalVisible(true), []);

  const onClickConfirmFold = () => {
    handlePlayerAction(PlayerActions.FOLD);
    closeFoldModalHandler();
  };

  const {
    canFold,
    canCheck,
    canCall,
    canBet,
    canRaise,
    canStandPatOrDiscard,
    preAmount,
    canPostBringIn,
    canComplete,
    preActionLabels,
  } = useAvailableActions(playerSeat, isActingPlayer);

  const checkingOrCallingAmount = tableState?.handState?.state.checking_or_calling_amount;

  const minCompletionBettingOrRaisingToAmount =
    tableState?.handState?.state.min_completion_betting_or_raising_to_amount;

  const maxCompletionBettingOrRaisingToAmount =
    tableState?.handState?.state.max_completion_betting_or_raising_to_amount;

  const [betOrRaiseAmount, setBetOrRaiseAmount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (maxCompletionBettingOrRaisingToAmount !== undefined) {
      setBetOrRaiseAmount(minCompletionBettingOrRaisingToAmount);
    }
  }, [minCompletionBettingOrRaisingToAmount]);

  const betStructure = tableState?.currentVariant.bettingStructure;

  const isFixedLimitBetting = betStructure === BettingStructure.FIXED_LIMIT;

  const isOnBet = useMemo(() => {
    return Math.max(...(tableState?.handState?.state?.bets || [])) > (tableState?.bigBlind || 0);
  }, [tableState?.handState?.state?.bets, tableState?.bigBlind]);

  const isPreFlop = useMemo(() => {
    return !isOnBet && tableState?.handState?.state.street_index === 0;
  }, [isOnBet, tableState?.handState?.state.street_index]);

  const handPhase = useMemo(() => {
    if (isOnBet) {
      return HandPhases.ON_BET;
    } else if (isPreFlop) {
      return HandPhases.PRE_FLOP;
    } else {
      return HandPhases.POST_FLOP;
    }
  }, [isOnBet, isPreFlop]);

  const currentBet = useMemo(() => {
    return Math.max(...(tableState?.handState?.state?.bets || []));
  }, [tableState?.handState?.state?.bets]);

  const potCompletionBettingOrRaisingToAmount = useMemo(() => {
    return tableState?.handState?.state?.pot_completion_betting_or_raising_to_amount;
  }, [tableState?.handState?.state?.pot_completion_betting_or_raising_to_amount]);

  const bringInAmount = useMemo(() => {
    return tableState?.handState?.state.effective_bring_in_amount;
  }, [tableState?.handState?.state.effective_bring_in_amount]);

  const shouldShowActionButton = useMemo(() => {
    return !(isMobileView && showSlider);
  }, [isMobileView, showSlider]);

  const shouldShowBetSlider = useMemo(() => {
    return (
      betStructure !== BettingStructure.FIXED_LIMIT &&
      minCompletionBettingOrRaisingToAmount !== maxCompletionBettingOrRaisingToAmount
    );
  }, [betStructure, minCompletionBettingOrRaisingToAmount, maxCompletionBettingOrRaisingToAmount]);

  const showAllIn = useMemo(() => {
    return betStructure === BettingStructure.NO_LIMIT || maxCompletionBettingOrRaisingToAmount === playerSeat?.stack;
  }, [betStructure, maxCompletionBettingOrRaisingToAmount, playerSeat?.stack]);

  const betSliderStep = useMemo(() => {
    return user.settings?.stepInBigBlindsEnabled ? tableState?.bigBlind! : tableState.smallBlind;
  }, [user.settings?.stepInBigBlindsEnabled, tableState?.bigBlind, tableState.smallBlind]);

  const handleBetOrRaiseAmountChange = useCallback(
    (amount: number) => {
      if (amount >= minCompletionBettingOrRaisingToAmount!) {
        setBetOrRaiseAmount(amount);
      } else {
        setBetOrRaiseAmount(minCompletionBettingOrRaisingToAmount);
      }
    },
    [minCompletionBettingOrRaisingToAmount],
  );

  // Handle player actions, including pre-actions and actual actions
  const handlePlayerAction = useCallback(
    (action: PlayerActions, amount?: number) => {
      if (!isActingPlayer) {
        // Set or clear pre-action when it's not the player's turn
        setPreAction((prev) => (prev === action ? '' : action)); // Clear pre-action if clicking the same button again
        setPreActionCallAmount(maxBet);
      } else {
        const nextPreactions = getPreActions(tableState!, playerSeat!);
        const completeActions: AvailableActions = {
          ...defaultActions,
          ...nextPreactions,
        };

        const updateTableState = (subtype?: string) => {
          if (!tableState?.handState) {
            return;
          }

          const tableActionPerformedMsg: TableActionPerformedMsg = {
            type: TableIncomingEvents.ACTION_PERFORMED,
            tableId,
            data: {
              lastOperation: {
                type: 'Operation',
                subtype: subtype || '',
                action: subtype !== PlayerActions.STAND_PAT ? action : PlayerActions.STAND_PAT,
              },
            },
          };

          const sound = getTableEventSound(tableActionPerformedMsg);

          const lastOperationLabel = {
            label: subtype !== PlayerActions.STAND_PAT ? action : PlayerActions.STAND_PAT,
            playerIndex: tableState.playerSeat?.playerIndex || 0,
            seat: tableState.playerSeat?.seat || 0,
          };

          return dispatch(
            tableStateUpdate(
              tableId,
              {
                ...tableState,
                lastOperationLabel,
                sound,
                playerSeat: {
                  ...playerSeat,
                  isActingPlayer: false,
                  availableActions: nextPreactions ? completeActions : undefined,
                },
              },
              true,
            ),
          );
        };

        let subTypeOperation = '';

        // Execute the action when it's the player's turn and conditions are met
        switch (action) {
          case PlayerActions.COMPLETE:
            dispatch(completeAction(tableId, amount));
            break;
          case PlayerActions.POST_BRING_IN:
            dispatch(bringInAction(tableId));
            break;

          case PlayerActions.CHECK:
            subTypeOperation = Operations.CheckingOrCalling;
            dispatch(checkAction(tableId));
            break;
          case PlayerActions.CALL:
            subTypeOperation = Operations.CheckingOrCalling;
            dispatch(callAction(tableId));
            break;
          case PlayerActions.BET:
            dispatch(betAction(tableId, amount));
            break;
          case PlayerActions.RAISE:
            dispatch(raiseAction(tableId, amount));
            break;
          case PlayerActions.FOLD:
            // Save fold cards before folding
            const cards = playerSeat?.cardIndexes.map(getCardFromIndex);

            dispatch(playerSaveFoldCardsAction(tableId, TableIncomingEvents.SAVE_FOLD_CARDS, user.id!, cards || []));
            dispatch(foldAction(tableId));
            break;
          case PlayerActions.DISCARD:
            if (player?.cardIndexesForDiscard.length === 0) {
              subTypeOperation = PlayerActions.STAND_PAT;
            }
            setPreAction(''); // Clear pre-action for discard
            dispatch(discardAction(tableId, player?.cardIndexesForDiscard));
            break;
          case PlayerActions.TIMEBANK:
            dispatch(timebankAction(tableId));
            break;
        }

        updateTableState(subTypeOperation);
      }
    },
    [
      player?.cardIndexesForDiscard,
      dispatch,
      getCardFromIndex,
      isActingPlayer,
      maxBet,
      playerSeat?.cardIndexes,
      tableId,
      user.id,
    ],
  );

  // Effect to handle pre-actions when it becomes the player's turn
  useEffect(() => {
    if (!isActingPlayer || preAction === '') return;

    if (preAction === PlayerActions.FOLD && canCheck) {
      handlePlayerAction(PlayerActions.CHECK);
    } else if (preAction === PlayerActions.CHECK || preAction === PlayerActions.CALL) {
      if (canCheck) {
        handlePlayerAction(PlayerActions.CHECK);
      } else if (canCall && preActionCallAmount === undefined) {
        handlePlayerAction(PlayerActions.CALL);
      } else if (canCall && preActionCallAmount === checkingOrCallingAmount) {
        handlePlayerAction(PlayerActions.CALL);
      } else if (canCall && preActionCallAmount !== checkingOrCallingAmount) {
        // If the pre-action is a call but the amount has changed, clear the pre-action
      }
    } else {
      handlePlayerAction(preAction as PlayerActions, betOrRaiseAmount);
    }
    // Clear the pre-action after execution or skipping
    setPreAction('');
    setPreActionCallAmount(undefined);
  }, [isActingPlayer, preAction, betOrRaiseAmount, canCheck, canCall]);

  // Effect to reset pre-action call when the maximum bet value changes
  useEffect(() => {
    if (preAction === PlayerActions.CHECK && !canCheck && canCall) {
      // If the pre-action was CHECK (from check/fold) but now we can't check and can call,
      // update the pre-action to CALL and set the call amount
      setPreAction(PlayerActions.CALL);
      setPreActionCallAmount(maxBet);
    } else if (
      preAction === PlayerActions.CALL &&
      maxBet !== preActionCallAmount &&
      preActionCallAmount !== undefined
    ) {
      if (!isFixedLimitBetting) {
        // If the pre-action is CALL but the bet size has changed, clear the pre-action
        setPreActionCallAmount(undefined);
        setPreAction('');
      }
    }
  }, [maxBet, preAction, preActionCallAmount]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!userHotkeys) return;

      const hotkeys = new Map();

      Object.entries(userHotkeys).forEach(([action, hotkey]) => {
        hotkeys.set(action, hotkey);
      });

      const isCtrlOrMetaPressed = event.ctrlKey || event.metaKey;

      for (const [action, hotkey] of hotkeys.entries()) {
        if (
          hotkey.keys.includes(event.code) &&
          (!hotkey.ctrl || isCtrlOrMetaPressed) &&
          (!hotkey.meta || isCtrlOrMetaPressed)
        ) {
          switch (action) {
            case 'fold':
              if (canFold) {
                if (canCheck && !!isActingPlayer) {
                  openFoldModalHandler();
                } else {
                  handlePlayerAction(PlayerActions.FOLD);
                }
              }
              break;
            case 'call':
              if (canCall) {
                return handlePlayerAction(PlayerActions.CALL);
              }
              if (canCheck) {
                return handlePlayerAction(PlayerActions.CHECK);
              }
              break;
            case 'check':
              if (canCheck) {
                return handlePlayerAction(PlayerActions.CHECK);
              }
              if (canCall) {
                return handlePlayerAction(PlayerActions.CALL);
              }
              break;
            case 'bringIn':
              if (canPostBringIn) {
                handlePlayerAction(PlayerActions.POST_BRING_IN);
              }
              break;
            case 'betOrRaise':
              if (canRaise || canBet) {
                if (isFixedLimitBetting) {
                  handlePlayerAction(PlayerActions.RAISE, betOrRaiseAmount);
                } else {
                  setShowSlider(true);
                }
              }
              break;
            case 'complete':
              if (canComplete) {
                handlePlayerAction(PlayerActions.COMPLETE);
              }
              break;
            case 'confirm':
              // TODO right now confirm is by default assigned to the enter key
              if (canRaise && showSlider && betOrRaiseAmount && isMobileView) {
                setShowSlider(false);
                handlePlayerAction(PlayerActions.RAISE, betOrRaiseAmount);
              }
              break;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    userHotkeys,
    canFold,
    canCall,
    canCheck,
    canPostBringIn,
    canRaise,
    canBet,
    canComplete,
    isActingPlayer,
    betOrRaiseAmount,
    showSlider,
    handlePlayerAction,
    openFoldModalHandler,
    setShowSlider,
    isFixedLimitBetting,
    isMobileView,
  ]);

  const { shouldDisplayInBB } = useBBDisplay({
    tableId,
  });

  if (!tableState || !tableState.handState) {
    return null;
  }

  return (
    <div className={!isMobileView ? styles.gridContainer : ''}>
      <div className={styles.actionButtonsContainer}>
        <div className={styles.betRaiseButton}>
          <div className="h-5 min-w-[7.6875em]">
            {!!isActingPlayer && (
              <TimeBankButton
                tableId={tableId}
                isUsingTimebank={isUsingTimebank}
                timebank={playerSeat?.timebank}
                isActingPlayer={isActingPlayer}
                seat={seat}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-col justify-between gap-2 text-ph-gray-silver">
        <div className={twMerge(styles.actionButtonsContainer, 'gap-1')}>
          {/* Fold Button */}

          {canFold && shouldShowActionButton && (
            <ActionButton
              variant={ActionButtonVariant.Fold}
              isChecked={preAction === PlayerActions.FOLD}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canFold}
              onClick={() => {
                if (canCheck && !!isActingPlayer) {
                  openFoldModalHandler();
                } else {
                  handlePlayerAction(PlayerActions.FOLD);
                }
              }}
              containerClassName={styles.foldButton}
            >
              <span>{preActionLabels.foldCheck?.label || 'Fold'}</span>
            </ActionButton>
          )}

          {/* Check Button */}
          {!canCall && canCheck && shouldShowActionButton && (
            <ActionButton
              variant={ActionButtonVariant.Check}
              isChecked={preAction === PlayerActions.CHECK}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canCheck}
              onClick={() => handlePlayerAction(PlayerActions.CHECK)}
              containerClassName={styles.checkCallButton}
            >
              <span>{preActionLabels.checkCall?.label || 'Check'}</span>
            </ActionButton>
          )}
          {/* Call Button */}
          {!canCheck && canCall && shouldShowActionButton && (
            <ActionButton
              variant={ActionButtonVariant.Call}
              isChecked={preAction === PlayerActions.CALL}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canCall}
              onClick={() => handlePlayerAction(PlayerActions.CALL)}
              containerClassName={styles.checkCallButton}
            >
              <span className="flex items-end">
                {preActionLabels.checkCall?.label || (
                  <>
                    <span className="ml-1 flex flex-row items-center gap-0.5">
                      Call
                      <CurrencyLabel
                        amount={isActingPlayer ? checkingOrCallingAmount! : preAmount!}
                        bigBlindValue={tableState.bigBlind!}
                        shouldDisplayInBB={shouldDisplayInBB!}
                      />
                    </span>
                  </>
                )}
              </span>
            </ActionButton>
          )}
          {/* Post Bring In Button */}
          {canPostBringIn && (
            <ActionButton
              variant={ActionButtonVariant.PostBringIn}
              isChecked={preAction === PlayerActions.POST_BRING_IN}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canPostBringIn}
              onClick={() => handlePlayerAction(PlayerActions.POST_BRING_IN)}
              containerClassName={styles.bringInButton}
            >
              <span className="flex flex-row items-center whitespace-nowrap">
                Bring In
                <CurrencyLabel
                  amount={bringInAmount!}
                  bigBlindValue={tableState.bigBlind!}
                  shouldDisplayInBB={shouldDisplayInBB!}
                />
              </span>
            </ActionButton>
          )}
          {/* Complete Button */}
          {canComplete && shouldShowActionButton && (
            <ActionButton
              variant={ActionButtonVariant.Complete}
              isChecked={preAction === PlayerActions.COMPLETE}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canComplete}
              onClick={() => handlePlayerAction(PlayerActions.COMPLETE, betOrRaiseAmount)}
              containerClassName={styles.completeButton}
            >
              <span className="flex flex-row items-center">
                Complete
                <CurrencyLabel
                  amount={maxCompletionBettingOrRaisingToAmount!}
                  bigBlindValue={tableState.bigBlind!}
                  shouldDisplayInBB={shouldDisplayInBB!}
                />
              </span>
            </ActionButton>
          )}
          {/* Discard Button */}
          {canStandPatOrDiscard && (
            <ActionButton
              variant={ActionButtonVariant.Discard}
              isChecked={preAction === PlayerActions.DISCARD}
              isPlayerTurn={!!isActingPlayer}
              isDisabled={!canStandPatOrDiscard}
              onClick={() => handlePlayerAction(PlayerActions.DISCARD)}
              containerClassName={styles.standPatDiscarButton}
            >
              <div className="flex flex-row items-center justify-center gap-1">
                <span>{cardsForDiscard.length > 0 ? 'Discard' : 'Stand pat'}</span>
                <div className="flex items-center justify-center gap-[1px]">
                  {cardsForDiscard &&
                    cardsForDiscard.map((card, index) => (
                      <CardFace
                        key={`option-${card.rank}${card.suit}-${index}`}
                        rank={card.rank}
                        suit={card.suit}
                        isButtonCard={true}
                        isElevated={false}
                        size={CardSizes.xxs}
                      />
                    ))}
                </div>
              </div>
            </ActionButton>
          )}
          {/* Raise or Bet Button */}

          {(canRaise || canBet) && (
            <RaiseActionButton
              shouldDisplayInBB={shouldDisplayInBB!}
              isPlayerTurn={!!isActingPlayer}
              isChecked={preAction === PlayerActions.RAISE || preAction === PlayerActions.BET}
              smallBlindValue={tableState.smallBlind}
              currentBet={isActingPlayer ? currentBet : playerSeat?.preActionsAvailable?.betRaise?.amount!}
              max={maxCompletionBettingOrRaisingToAmount!}
              pot={potCompletionBettingOrRaisingToAmount!}
              min={minCompletionBettingOrRaisingToAmount!}
              bigBlindValue={tableState?.bigBlind!}
              handPhase={handPhase}
              showAllIn={showAllIn}
              onChange={handleBetOrRaiseAmountChange}
              onClick={
                canBet
                  ? () => handlePlayerAction(PlayerActions.BET, betOrRaiseAmount)
                  : () => handlePlayerAction(PlayerActions.RAISE, betOrRaiseAmount)
              }
              betOrRaiseAmount={betOrRaiseAmount!}
              canFold={!!canFold}
              setShowBetSlider={isFixedLimitBetting ? () => {} : setShowSlider}
              showBetSlider={showSlider}
              shouldShowBetSlider={!isFixedLimitBetting}
              canRaise={canRaise}
              className={shouldShowActionButton ? styles.betRaiseButton : styles.betRaiseButtonFull}
            />
          )}
        </div>

        {!isMobileView && shouldShowBetSlider && (
          <BetSlider
            min={minCompletionBettingOrRaisingToAmount!}
            max={maxCompletionBettingOrRaisingToAmount!}
            step={betSliderStep}
            onChange={handleBetOrRaiseAmountChange}
            value={betOrRaiseAmount!}
            bigBlindValue={tableState?.bigBlind!}
            handPhase={handPhase}
            pot={potCompletionBettingOrRaisingToAmount!}
            currentBet={isActingPlayer ? currentBet : playerSeat?.preActionsAvailable?.betRaise?.amount!}
            showAllIn={showAllIn}
            isPlayerTurn={!!isActingPlayer}
            setShowBetSlider={() => {}}
            tableId={tableId}
            canRaise={canRaise}
            canBet={canBet}
            shouldDisplayInBB={shouldDisplayInBB}
          />
        )}

        <div
          className={twMerge(
            'flex w-full flex-row items-center justify-center gap-2',
            isMobileViewSm && !showSlider ? 'hidden' : '',
          )}
        >
          {shouldShowBetSlider && !isMobileView && (
            <QuickActionButtons
              currentBet={currentBet}
              max={maxCompletionBettingOrRaisingToAmount!}
              pot={potCompletionBettingOrRaisingToAmount!}
              min={minCompletionBettingOrRaisingToAmount!}
              bigBlindValue={tableState?.bigBlind!}
              handPhase={handPhase}
              showAllIn={
                betStructure === BettingStructure.NO_LIMIT ||
                maxCompletionBettingOrRaisingToAmount === playerSeat?.stack
              }
              onChange={handleBetOrRaiseAmountChange}
              setShowBetSlider={setShowSlider}
              isPlayerTurn={!!isActingPlayer}
            />
          )}
        </div>

        <ConfirmationModal
          open={foldModalVisible}
          onClose={closeFoldModalHandler}
          onClickConfirm={onClickConfirmFold}
          message="Are you sure you want to fold this hand? You can check!"
          confirmText="Yes, fold"
          cancelText="No, keep playing"
        />
      </div>
    </div>
  );
};

export default UserActions;
